import { Button, Form, FormField, FormGroup, FormLabel, Loader } from '@ax/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { FieldError, SubmitHandler, useForm } from 'react-hook-form';
import { VisitFormInputSchema } from './VisitFormInputSchema';
import {
  useGetAllAppointmentsApi,
  useSaveAppointmentsApi,
  useSaveEHRAppointmentApi,
  useUpdateEHRAppointmentApi,
} from '@/hooks/api';
import { Auth } from '@ax/auth';
import toast from 'react-hot-toast';
import { VisitModalData } from '../VisitFormModal';
import { format, parse } from 'date-fns';
import { selectIsEdEnabled, useAppSelector } from '@ax/redux-store';
import useAnalytics from '@/hooks/useAnalytics';
import { NOTE_STATUS } from '@ax/constants';
import { addDaysToDate, concatenateDateTime } from '@/utils';
import SelectField from '@ax/ui/src/components/SelectField';
import { GENDER_CONSTANTS } from '@/constants/gender-constants';

interface VisitFormInput {
  doctorId: number;
  date: string;
  startTime: string;
  roomNumber?: string;
  patient: {
    firstName: string;
    patientAge?: number;
    gender?: string;
    chiefComplaint?: string;
  };
}

interface VisitFormProps {
  modalClose: () => void;
  refetchData?: () => void;
  modalData?: VisitModalData;
}

const VisitForm: React.FC<VisitFormProps> = ({ modalClose, refetchData, modalData }) => {
  const analytics = useAnalytics();
  const selectedVisitDate = useAppSelector((state) => state.selectedDate);
  const { data: visitList }: any = useGetAllAppointmentsApi(
    Auth.currentUser()?.uid,
    selectedVisitDate.date,
  );

  const isEdEnabled = useAppSelector(selectIsEdEnabled);

  const visitFormDefaultValues = () => {
    const date = selectedVisitDate?.date;
    const startTime = modalData?.appointmentStartTime
      ? format(parse(modalData.appointmentStartTime, 'hh:mm a', new Date()), 'HH:mm')
      : format(new Date(), 'HH:mm');
    const firstName =
      modalData?.patientName ?? `New patient ${(visitList?.dataList?.length ?? 0) + 1}`;
    const patientAge = modalData?.patientAge ?? 1;
    const gender = modalData?.patientGender;
    const chiefComplaint = modalData?.chiefComplaint;
    const roomNumber = modalData?.roomNumber;

    return {
      date,
      startTime,
      roomNumber,
      patient: {
        firstName,
        patientAge,
        gender,
        chiefComplaint,
      },
    };
  };

  const {
    watch,
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<VisitFormInput>({
    resolver: zodResolver(VisitFormInputSchema),
    defaultValues: visitFormDefaultValues(),
  });

  const { mutateAsync: saveEHRAppointment, isLoading: isSavingEHRAppointment } =
    useSaveEHRAppointmentApi();
  const { mutateAsync: updateEHRAppointment, isLoading: isUpdatingEHRAppointment } =
    useUpdateEHRAppointmentApi();
  const { mutateAsync: saveAppointment, isLoading: isSavingAppointment } = useSaveAppointmentsApi();

  const onSubmit: SubmitHandler<VisitFormInput> = async (data) => {
    try {
      let res;
      const appointmentData = { ...data, doctorId: Auth.currentUser()?.uid };

      if (!modalData?.uuid) {
        res = await saveEHRAppointment(appointmentData);

        if (res) {
          const visitDate = selectedVisitDate?.date;
          const currentTime = format(new Date(), 'hh:mm a');
          const formattedServiceDate = concatenateDateTime(visitDate, currentTime);

          await saveAppointment([
            {
              appointmentId: res?.data?.uuid,
              noteStatus: NOTE_STATUS.NEW_APPOINTMENT,
              serviceDate: formattedServiceDate,
              expirationDate: addDaysToDate(formattedServiceDate, 30),
            },
          ]);
        }

        const analyticsProperties = {
          visitDateTime: `${res?.data.appointmentDate}, ${res?.data.appointmentStartTime}`,
          noteId: res?.data.uuid,
        };

        analytics.onVisitAddition(analyticsProperties);
      } else {
        res = await updateEHRAppointment({
          ...appointmentData,
          appointmentId: modalData?.uuid,
        });
      }

      modalClose?.();
      refetchData?.();

      if (res?.message === 'success') {
        toast.success(!modalData?.uuid ? 'Visit added successfully' : 'Visit updated successfully');
      }
    } catch (error) {
      console.error(error);
      toast.error('An error occurred, please try again');
    }
  };

  const genderOptions = Object.keys(GENDER_CONSTANTS).map((key) => ({
    value: GENDER_CONSTANTS[key].value,
    label: GENDER_CONSTANTS[key].label,
  }));

  const renderButtonContent = () => {
    return isSavingEHRAppointment || isSavingAppointment || isUpdatingEHRAppointment ? (
      <Loader
        color="#fff"
        text={'Saving...'}
      />
    ) : (
      'Save'
    );
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <FormGroup className="mb-[10px]">
        <FormLabel className="input__label">Patient Name</FormLabel>
        <FormField error={errors.patient?.firstName as FieldError}>
          <input
            type="text"
            className="input"
            placeholder="Add name"
            {...register('patient.firstName')}
          />
        </FormField>
      </FormGroup>
      <div className="grid grid-cols-12 gap-3 mb-[10px]">
        <FormGroup className="col-span-6">
          <FormLabel className="input__label">Visit Date</FormLabel>
          <FormField error={errors.date as FieldError}>
            <input
              type="date"
              className="input"
              {...register('date')}
              disabled
            />
          </FormField>
        </FormGroup>
        <FormGroup className="col-span-6">
          <FormLabel className="input__label">Visit Time</FormLabel>
          <FormField error={errors.startTime as FieldError}>
            <input
              type="time"
              className="input"
              {...register('startTime')}
            />
          </FormField>
        </FormGroup>
      </div>
      {isEdEnabled && (
        <>
          <div className="grid grid-cols-12 gap-3 mb-[10px]">
            <FormGroup className="col-span-6">
              <FormLabel className="input__label">Age</FormLabel>
              <FormField error={errors.patient?.patientAge as FieldError}>
                <input
                  type="number"
                  className="input"
                  placeholder="Add age"
                  {...register('patient.patientAge')}
                />
              </FormField>
            </FormGroup>
            <FormGroup className="col-span-6">
              <FormLabel className="input__label">Gender</FormLabel>
              <FormField error={errors.patient?.gender as FieldError}>
                <SelectField
                  name="patient.gender"
                  options={genderOptions}
                  value={genderOptions.find((option) => option.value === watch('patient.gender'))}
                  control={control}
                />
              </FormField>
            </FormGroup>
          </div>
          <div className="grid grid-cols-12 gap-3 mb-[10px]">
            <FormGroup className="col-span-12">
              <FormLabel className="input__label">Room #</FormLabel>
              <FormField error={errors.roomNumber as FieldError}>
                <input
                  type="text"
                  className="input"
                  placeholder="Add room #"
                  {...register('roomNumber')}
                />
              </FormField>
            </FormGroup>
          </div>
          <div className="grid grid-cols-12 gap-3 mb-[10px]">
            <FormGroup className="col-span-12">
              <FormLabel className="input__label">Chief Complaint</FormLabel>
              <FormField error={errors.patient?.chiefComplaint as FieldError}>
                <input
                  type="text"
                  className="input"
                  placeholder="Add chief complaint"
                  {...register('patient.chiefComplaint')}
                />
              </FormField>
            </FormGroup>
          </div>
        </>
      )}
      <Button
        className="h-[40px] mt-[10px]"
        variant="primary"
        block
        type="submit"
        disabled={isSavingEHRAppointment || isSavingAppointment || isUpdatingEHRAppointment}
      >
        {renderButtonContent()}
      </Button>
    </Form>
  );
};

export default VisitForm;
